/**
 * Eos Estimating Workbook link (backend apps.takeoff.workbook): types, the REST
 * client for a takeoff (`/api/documents/<id>/takeoff/`) or a board estimate
 * (`/api/boards/<id>/takeoff/`), and pure helpers for the push preview.
 */
import type { ApiClient } from './api';

export type WorkbookAction = 'add' | 'update' | 'unchanged' | 'conflict' | 'priced' | 'exists' | 'no_room' | 'remove' | 'stale';
/** Where a TYPE stands against the linked workbook's PROJECT BOM (item.workbook_state). */
export type WorkbookItemState = '' | 'pending' | 'linked' | 'removed';
export type WorkbookTab = 'PROJECT BOM' | 'AREA/ZONE BOM';

export interface WorkbookReadbackRow {
  row: number;
  type: string;
  qty: number | null;
  expected: number | null;
  category: string;
  lx_av: string;
  issues: ('qty_mismatch' | 'category_unresolved')[];
}
export interface WorkbookReadback {
  rows: WorkbookReadbackRow[];
  ok: boolean;
  issues: number;
}
export interface WorkbookPushSummary {
  counts?: Partial<Record<WorkbookAction, number>>;
  issues?: { tab: WorkbookTab; action: WorkbookAction; row: number | null; type: string; reason?: string }[];
  prefixes?: { cleared: string[]; unknown: string[] };
  error?: string;
}
export interface WorkbookLinkDTO {
  linked: boolean;
  template: { id: string; version: string };
  spreadsheet_id?: string;
  title?: string;
  url?: string;
  template_version?: string;
  created_copy?: boolean;
  linked_at?: string | null;
  linked_by?: string;
  last_push_at?: string | null;
  last_push_by?: string;
  last_push_status?: '' | 'running' | 'ok' | 'partial' | 'error';
  last_push?: WorkbookPushSummary;
  last_readback?: Partial<WorkbookReadback>;
  /** Two-way sync: the workbook owns the catalog (TYPEs, pricing…), the board owns the quantities. */
  auto_sync?: boolean;
  last_sync_at?: string | null;
  last_sync_status?: '' | 'syncing' | 'ok' | 'partial' | 'error';
  last_sync_error?: string;
  conflicts?: WorkbookConflict[];
  notices?: WorkbookNotice[];
  totals?: WorkbookTotals;
  suppressed?: { key: string; type?: string; area?: string; zone?: string; at?: string; kept?: string }[];
  /** Types deleted on the board whose PROJECT BOM row the workbook had filled in: kept there, not re-imported. */
  left_to_workbook?: { type: string; row?: number; at?: string }[];
  /** SCOPE choices: DASHBOARDS' scope register first, then any other SCOPE already used in AREA/ZONE BOM. */
  scopes?: string[];
  /** DASHBOARDS' scope register as read (BASE BID · BASE, OPTION n · OPTION…). */
  registered_scopes?: { name: string; type: string; add_to_quote: boolean | null }[];
}
export type WorkbookConflictKind = 'qty_edited' | 'type_edited' | 'type_removed' | 'type_renamed' | 'row_deleted' | 'duplicate_type' | 'scope_pinned';
export interface WorkbookConflict {
  id: string;
  kind: WorkbookConflictKind;
  message: string;
  type?: string;
  key?: string;
  item_id?: string;
  field?: string;
  sheet?: string;
  board?: string;
  area?: string;
  row?: number;
  placements?: number;
  at?: string;
}
export interface WorkbookNotice {
  kind: string;
  message: string;
  type?: string;
  at?: string;
}
export interface WorkbookTotals {
  available?: boolean;
  tab?: string;
  scopes?: { scope: string; equipment: string; total: string; budget_range: string }[];
  read_at?: string;
  error?: string;
}
/** `workbook.sync` socket frame: one finished sync (either direction). */
export interface WorkbookSyncFrame {
  status: '' | 'syncing' | 'ok' | 'partial' | 'error';
  error: string | null;
  synced_at: string;
  counts: Partial<Record<WorkbookAction, number>>;
  catalog: { linked?: number; new_from_sheet?: number; renamed?: number; removed?: number; added_to_sheet?: number };
  conflicts: WorkbookConflict[];
  new_conflicts: number;
  notices: WorkbookNotice[];
  totals: WorkbookTotals;
}
export interface WorkbookSuggestion {
  name: string;
  folder: { id: string; name: string } | null;
  path: string[];
  job: string;
  template: { id: string; version: string };
}
export interface WorkbookPlanAction {
  tab: WorkbookTab;
  key: string;
  type: string;
  action: WorkbookAction;
  row: number | null;
  reason?: string;
  values?: Record<string, string>;
  current?: Record<string, string>;
}
export interface WorkbookPlan {
  workbook: { id: string; title: string; url: string; version: string; columns_moved: Record<string, Record<string, number>> };
  actions: WorkbookPlanAction[];
  counts: Partial<Record<WorkbookAction, number>>;
  prefixes: { clear: { row: number; prefix: string }[]; custom: { row: number; prefix: string; duplicate: boolean; clear_range?: string }[]; unknown: string[] };
  clean_prefixes: boolean;
}
export interface WorkbookPushResult {
  success: boolean;
  status: 'ok' | 'partial' | 'error';
  error: string | null;
  pushed_at: string;
  summary: WorkbookPushSummary;
  readback: WorkbookReadback | null;
}

export function workbookApi(api: ApiClient, base: string) {
  const body = (method: string, data: unknown): RequestInit => ({ method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
  return {
    get: () => api.json<WorkbookLinkDTO>(`${base}workbook/`),
    link: (url: string) => api.json<WorkbookLinkDTO>(`${base}workbook/link/`, body('POST', { url })),
    suggest: () => api.json<WorkbookSuggestion>(`${base}workbook/create/`),
    create: (name: string, folderId?: string) => api.json<WorkbookLinkDTO>(`${base}workbook/create/`, body('POST', { name, folder_id: folderId ?? '' })),
    plan: (cleanPrefixes = true) => api.json<WorkbookPlan>(`${base}workbook/plan/?clean_prefixes=${cleanPrefixes ? 1 : 0}`),
    push: (cleanPrefixes = true) => api.json<{ status: 'queued' | 'running' }>(`${base}workbook/push/`, body('POST', { clean_prefixes: cleanPrefixes })),
    readback: () => api.json<WorkbookReadback>(`${base}workbook/readback/`, body('POST', {})),
    unlink: () => api.json<WorkbookLinkDTO>(`${base}workbook/`, { method: 'DELETE' }),
    setAutoSync: (on: boolean) => api.json<WorkbookLinkDTO>(`${base}workbook/`, body('PATCH', { auto_sync: on })),
    syncNow: () => api.json<{ status: 'queued' | 'running' }>(`${base}workbook/sync/`, body('POST', {})),
    touch: () => api.json<{ status: string }>(`${base}workbook/touch/`, body('POST', {})),
    resolve: (conflictId: string, action: WorkbookConflictAction) =>
      api.json<WorkbookLinkDTO>(`${base}workbook/conflicts/${encodeURIComponent(conflictId)}/`, body('POST', { action })),
    restore: (key: string) => api.json<WorkbookLinkDTO>(`${base}workbook/restore/`, body('POST', { key })),
  };
}
export type WorkbookApi = ReturnType<typeof workbookApi>;

/** True when a title is the template / release copy (mirrors the server guard). */
export function isTemplateTitle(title: string): boolean {
  return /template|release/i.test(title || '');
}

/** Google Drive folder id from a pasted folder link (or the id itself). */
export function parseFolderId(input: string): string | null {
  const v = (input || '').trim();
  const m = v.match(/\/folders\/([A-Za-z0-9_-]{10,})/) || v.match(/[?&]id=([A-Za-z0-9_-]{10,})/);
  if (m) return m[1];
  return /^[A-Za-z0-9_-]{10,}$/.test(v) ? v : null;
}

const WRITE: WorkbookAction[] = ['add', 'update', 'remove'];
const SKIP: WorkbookAction[] = ['conflict', 'priced', 'exists', 'no_room', 'stale'];

/** Rows a push would write, and rows it would leave alone with a reason. */
export function planSummary(plan: Pick<WorkbookPlan, 'actions' | 'prefixes' | 'clean_prefixes'>) {
  const count = (tab: WorkbookTab, acts: WorkbookAction[]) => plan.actions.filter((a) => a.tab === tab && acts.includes(a.action)).length;
  const prefixes = plan.clean_prefixes ? plan.prefixes.clear.length + plan.prefixes.custom.filter((c) => c.clear_range).length : 0;
  return {
    writes: plan.actions.filter((a) => WRITE.includes(a.action)).length,
    unchanged: plan.actions.filter((a) => a.action === 'unchanged').length,
    skipped: plan.actions.filter((a) => SKIP.includes(a.action)),
    projectBom: { add: count('PROJECT BOM', ['add']), update: count('PROJECT BOM', ['update']), remove: count('PROJECT BOM', ['remove']) },
    areaZone: { add: count('AREA/ZONE BOM', ['add']), update: count('AREA/ZONE BOM', ['update']), remove: count('AREA/ZONE BOM', ['remove']) },
    prefixes,
    nothingToDo: !plan.actions.some((a) => WRITE.includes(a.action)) && prefixes === 0,
  };
}

export const ACTION_LABEL: Record<WorkbookAction, string> = {
  add: 'Add',
  update: 'Update',
  unchanged: 'Unchanged',
  conflict: 'Edited in the workbook — skipped',
  priced: 'Priced — left alone',
  exists: 'Already in the workbook — skipped',
  no_room: 'Table full — skipped',
  remove: 'Clear (no longer in the takeoff)',
  stale: 'No longer in the takeoff — left alone',
};

/** "Synced with workbook · 12 s ago" / "Syncing…" / the error, for the status line. */
export function syncStatusText(l: Pick<WorkbookLinkDTO, 'linked' | 'auto_sync' | 'last_sync_at' | 'last_sync_status' | 'last_sync_error'>, now = Date.now()): string {
  if (!l.linked) return 'No workbook linked';
  if (l.last_sync_status === 'syncing') return 'Syncing with workbook…';
  if (l.last_sync_status === 'error') return `Workbook sync failed: ${l.last_sync_error || 'unknown error'}`;
  if (!l.last_sync_at) return l.auto_sync === false ? 'Auto-sync off · not synced yet' : 'Waiting for the first sync…';
  const s = Math.max(0, Math.round((now - Date.parse(l.last_sync_at)) / 1000));
  const ago = s < 5 ? 'just now' : s < 60 ? `${s} s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`;
  return `${l.auto_sync === false ? 'Auto-sync off · last synced' : 'Synced with workbook'} · ${ago}`;
}

export type WorkbookConflictAction = 'dismiss' | 'keep_sheet' | 'restore' | 'use_board';

export const CONFLICT_ACTIONS: Record<WorkbookConflictKind, { action: WorkbookConflictAction; label: string }[]> = {
  qty_edited: [
    { action: 'dismiss', label: 'Keep board' },
    { action: 'keep_sheet', label: 'Keep sheet' },
  ],
  type_edited: [
    { action: 'dismiss', label: 'Keep board' },
    { action: 'keep_sheet', label: 'Keep sheet' },
  ],
  row_deleted: [
    { action: 'restore', label: 'Write it again' },
    { action: 'dismiss', label: 'Leave it out' },
  ],
  type_removed: [
    { action: 'restore', label: 'Re-add TYPE to workbook' },
    { action: 'dismiss', label: 'OK' },
  ],
  type_renamed: [{ action: 'dismiss', label: 'OK' }],
  duplicate_type: [{ action: 'dismiss', label: 'OK' }],
  scope_pinned: [
    { action: 'use_board', label: 'Use the board’s SCOPE' },
    { action: 'dismiss', label: 'Keep sheet' },
  ],
};
